import { useEffect, useState } from 'react';
import { Dialog, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import { registrarPagoCobro } from '../../services/cobranzaService';
import { colorPrimarioTramite } from '../Documentos/coloresTramite';
import { formatoFecha, formatoMoneda } from './PanelesCobro';
import InputMonto from '../InputMonto';
import { hoyISO, montoImputado, pendienteDeCuota } from './planCuotas';

const COLOR = colorPrimarioTramite('cobro');
const MEDIOS = { EFECTIVO: 'Efectivo', TRANSFERENCIA: 'Transferencia', DEPOSITO: 'Depósito', CHEQUE: 'Cheque', OTRO: 'Otro' };

function Dato({ etiqueta, children, destacado }) {
    return (
        <div className="col-6">
            <div className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.03em' }}>{etiqueta}</div>
            <div className={destacado ? 'fw-bold fs-5' : 'fw-semibold'} style={{ color: destacado ? COLOR : '#1e293b' }}>{children}</div>
        </div>
    );
}

/** "Vence hoy", "Vence en 5 días" o "Vencida hace 3 días": lo que importa al cobrar. */
function textoVencimiento(cuota) {
    if (!cuota.fechaVencimiento) return null;
    const dias = differenceInCalendarDays(parseISO(cuota.fechaVencimiento), new Date());
    if (dias === 0) return { texto: 'Vence hoy', vencida: false };
    if (dias > 0) return { texto: `Vence en ${dias} ${dias === 1 ? 'día' : 'días'}`, vencida: false };
    return { texto: `Vencida hace ${-dias} ${-dias === 1 ? 'día' : 'días'}`, vencida: true };
}

/**
 * Cobrar una cuota: muestra su detalle (número, vencimiento, importe, lo que ya se pagó y lo que
 * falta) y recién al confirmar registra el pago, imputado a esa cuota. El monto viene con lo que
 * falta de la cuota, pero se puede cambiar: un pago menor la deja con saldo, y el modal lo avisa.
 *
 * El pago queda asentado solo en la bitácora del cobro (lo hace el backend, ver
 * CobroService.registrarPago). Para lo que no es de una cuota (entrega inicial, cancelación
 * anticipada) sigue estando ModalRegistrarPago.
 *
 * La cuota no tiene interés ni recargo por mora en el modelo, así que acá no se calcula ninguno.
 *
 * @param cuota          la cuota a pagar (del detalle del cobro)
 * @param cantidadCuotas cuántas cuotas vigentes tiene el plan, para mostrar "cuota 2 de 5"
 * @param pagos          los pagos del cobro, para saber lo que ya se imputó a esta cuota
 */
export default function ModalPagarCuota({ open, idCobro, cuota, cantidadCuotas, pagos = [], onClose, onGuardado }) {
    const pendiente = pendienteDeCuota(cuota, pagos);
    const yaPagado = montoImputado(cuota, pagos);

    const [form, setForm] = useState({ monto: pendiente, fecha: hoyISO(), medio: 'TRANSFERENCIA', observacion: '' });
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({ monto: pendienteDeCuota(cuota, pagos), fecha: hoyISO(), medio: 'TRANSFERENCIA', observacion: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const monto = Number(form.monto) || 0;
    const vencimiento = textoVencimiento(cuota);

    const handleConfirmar = async () => {
        if (!(monto > 0)) {
            toast.error('Ingresá un monto válido.');
            return;
        }
        setGuardando(true);
        try {
            await registrarPagoCobro(idCobro, {
                fecha: form.fecha,
                monto,
                medio: form.medio,
                esEntregaInicial: false,
                idCuota: cuota.id,
                observacion: form.observacion?.trim() || null,
            });
            toast.success(`Pago de la cuota ${cuota.numero} registrado.`);
            onGuardado?.();
            onClose?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo registrar el pago.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflowY: 'auto' } }}>
            <Box sx={{ background: `${COLOR}12`, borderBottom: `1px solid ${COLOR}33`, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <span className="fw-bold" style={{ color: '#1e293b', fontSize: '1.05rem' }}>
                    <i className="bi bi-cash-coin me-2" style={{ color: COLOR }}></i>Pagar cuota {cuota.numero}
                </span>
                <IconButton onClick={onClose} size="small" aria-label="Cerrar"><CloseIcon fontSize="small" /></IconButton>
            </Box>
            {/* Cuerpo con scroll propio y botones fijos abajo (ver ModalPlanCuotas): en una pantalla baja el
                formulario no entra entero y "Confirmar pago" no puede quedar cortado. */}
            <Box sx={{ p: 3, overflowY: 'auto' }}>
                <div className="rounded-4 p-3 mb-4" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div className="row g-3">
                        <Dato etiqueta="Cuota">{cuota.numero}{cantidadCuotas ? ` de ${cantidadCuotas}` : ''}</Dato>
                        <Dato etiqueta="Vencimiento">
                            {formatoFecha(cuota.fechaVencimiento)}
                            {vencimiento && (
                                <div className="small fw-semibold" style={{ color: vencimiento.vencida ? '#991b1b' : '#64748b' }}>{vencimiento.texto}</div>
                            )}
                        </Dato>
                        <Dato etiqueta="Importe de la cuota">{formatoMoneda(cuota.monto)}</Dato>
                        {yaPagado > 0
                            ? <Dato etiqueta="Ya pagado">{formatoMoneda(yaPagado)}</Dato>
                            : <div className="col-6" />}
                        <Dato etiqueta="Pendiente de la cuota" destacado>{formatoMoneda(pendiente)}</Dato>
                    </div>
                    {cuota.observacion && <div className="small text-muted fst-italic mt-3">{cuota.observacion}</div>}
                </div>

                <div className="mb-3">
                    <label htmlFor="pago-monto" className="form-label fw-semibold">Monto a pagar</label>
                    <InputMonto id="pago-monto" value={form.monto} onChange={(v) => setForm((f) => ({ ...f, monto: v }))} />
                    {monto > 0 && monto < pendiente && (
                        <div className="form-text">Es un pago parcial: la cuota queda con {formatoMoneda(pendiente - monto)} pendiente.</div>
                    )}
                    {monto > pendiente && (
                        <div className="form-text text-warning-emphasis">Supera lo pendiente de la cuota por {formatoMoneda(monto - pendiente)}.</div>
                    )}
                </div>
                <div className="row g-3 mb-3">
                    <div className="col-6">
                        <label htmlFor="pago-fecha" className="form-label fw-semibold">Fecha del pago</label>
                        <input id="pago-fecha" type="date" className="form-control" value={form.fecha} onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))} />
                    </div>
                    <div className="col-6">
                        <label htmlFor="pago-medio" className="form-label fw-semibold">Medio</label>
                        <select id="pago-medio" className="form-select" value={form.medio} onChange={(e) => setForm((f) => ({ ...f, medio: e.target.value }))}>
                            {Object.entries(MEDIOS).map(([clave, etiqueta]) => <option key={clave} value={clave}>{etiqueta}</option>)}
                        </select>
                    </div>
                </div>
                <div>
                    <label htmlFor="pago-observacion" className="form-label fw-semibold">Observación</label>
                    <textarea id="pago-observacion" className="form-control" rows={2} value={form.observacion} onChange={(e) => setForm((f) => ({ ...f, observacion: e.target.value }))} />
                </div>
            </Box>
            <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 1, flexShrink: 0, background: '#fff' }}>
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={onClose} disabled={guardando}>Cancelar</button>
                <button className="btn rounded-pill px-4 fw-semibold text-white" style={{ background: COLOR, borderColor: COLOR }} onClick={handleConfirmar} disabled={guardando}>
                    {guardando ? <span className="spinner-border spinner-border-sm" /> : `Confirmar pago${monto > 0 ? ` de ${formatoMoneda(monto)}` : ''}`}
                </button>
            </Box>
        </Dialog>
    );
}
